import React, { useMemo, useState } from 'react';
import { clients } from '../../data/clients';
import { forecasts } from '../../data/forecasts';
import { MapPin, Clock, Route, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';
import { format, differenceInMinutes, subMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
interface OptimizedIntervention {
  clientId: string;
  forecastId: string;
  startTime: Date;
  endTime: Date;
  duration: number;
  travelTime: number;
  order: number;
  efficiency: number;
  slaCompliance: boolean;
}
export function RouteOptimizer() {
  const [selectedDay, setSelectedDay] = useState<string>('all');
  // Calculate optimized interventions
  const optimizedSchedule = useMemo(() => {
    const interventions: OptimizedIntervention[] = [];
    // Group forecasts by event start time
    const sortedForecasts = [...forecasts].filter(f => f.triggeredIntervention).sort((a, b) => a.eventStart.getTime() - b.eventStart.getTime());
    sortedForecasts.forEach((forecast, index) => {
      const client = clients.find(c => c.id === forecast.clientId);
      if (!client) return;
      // Calculate intervention duration based on accumulation
      const baseMinutes = 45;
      const accumulationFactor = forecast.accumulation.median / 5;
      const durationMinutes = Math.round(baseMinutes * (1 + accumulationFactor));
      // Calculate optimal start time (finish close to snow end)
      const optimalStartTime = subMinutes(forecast.eventEnd, durationMinutes + 15);
      // Adjust for SLA and priority
      let adjustedStartTime = optimalStartTime;
      if (client.priority === 'high') {
        adjustedStartTime = subMinutes(optimalStartTime, 30);
      }
      // Calculate travel time from previous location
      let travelTime = 0;
      if (index > 0) {
        const prevForecast = sortedForecasts[index - 1];
        const prevClient = clients.find(c => c.id === prevForecast.clientId);
        if (prevClient) {
          const distance = calculateDistance(client.address.coordinates, prevClient.address.coordinates);
          travelTime = Math.round(distance * 2);
        }
      }
      const startTime = new Date(adjustedStartTime.getTime() - travelTime * 60 * 1000);
      const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
      // Calculate efficiency (how close to snow end time)
      const timeDiff = Math.abs(differenceInMinutes(endTime, forecast.eventEnd));
      const efficiency = Math.max(0, 100 - timeDiff * 2);
      // Check SLA compliance
      const interventionDelay = differenceInMinutes(endTime, forecast.eventStart);
      const slaMinutes = client.slaHours * 60;
      const slaCompliance = interventionDelay <= slaMinutes;
      interventions.push({
        clientId: client.id,
        forecastId: forecast.id,
        startTime,
        endTime,
        duration: durationMinutes,
        travelTime,
        order: index + 1,
        efficiency,
        slaCompliance
      });
    });
    return interventions;
  }, []);
  // Calculate distance between two coordinates (simplified)
  function calculateDistance(coord1: {
    lat: number;
    lng: number;
  }, coord2: {
    lat: number;
    lng: number;
  }): number {
    const latDiff = Math.abs(coord1.lat - coord2.lat);
    const lngDiff = Math.abs(coord1.lng - coord2.lng);
    return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 100;
  }
  const avgEfficiency = optimizedSchedule.reduce((sum, i) => sum + i.efficiency, 0) / optimizedSchedule.length;
  const slaComplianceRate = optimizedSchedule.filter(i => i.slaCompliance).length / optimizedSchedule.length * 100;
  return <div className="space-y-6" data-id="element-478">
      {/* Optimization Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-479">
        <div className="flex items-center justify-between mb-6" data-id="element-480">
          <div data-id="element-481">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-482">
              Parcours optimisé
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-483">
              {optimizedSchedule.length} interventions planifiées
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-484">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium" data-id="element-485">
              Exporter le planning
            </button>
          </div>
        </div>
        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4" data-id="element-486">
          <div className="bg-green-50 rounded-lg p-4" data-id="element-487">
            <div className="flex items-center space-x-2 mb-2" data-id="element-488">
              <TrendingUp className="w-5 h-5 text-green-600" data-id="element-489" />
              <span className="text-sm font-medium text-green-900" data-id="element-490">
                Efficacité moyenne
              </span>
            </div>
            <p className="text-2xl font-bold text-green-600" data-id="element-491">
              {avgEfficiency.toFixed(1)}%
            </p>
            <p className="text-xs text-green-700 mt-1" data-id="element-492">
              Proximité avec fin de neige
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4" data-id="element-493">
            <div className="flex items-center space-x-2 mb-2" data-id="element-494">
              <CheckCircle className="w-5 h-5 text-blue-600" data-id="element-495" />
              <span className="text-sm font-medium text-blue-900" data-id="element-496">
                Conformité SLA
              </span>
            </div>
            <p className="text-2xl font-bold text-blue-600" data-id="element-497">
              {slaComplianceRate.toFixed(0)}%
            </p>
            <p className="text-xs text-blue-700 mt-1" data-id="element-498">
              {optimizedSchedule.filter(i => i.slaCompliance).length}/
              {optimizedSchedule.length} clients
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4" data-id="element-499">
            <div className="flex items-center space-x-2 mb-2" data-id="element-500">
              <Route className="w-5 h-5 text-purple-600" data-id="element-501" />
              <span className="text-sm font-medium text-purple-900" data-id="element-502">
                Temps de trajet
              </span>
            </div>
            <p className="text-2xl font-bold text-purple-600" data-id="element-503">
              {Math.round(optimizedSchedule.reduce((sum, i) => sum + i.travelTime, 0))}{' '}
              min
            </p>
            <p className="text-xs text-purple-700 mt-1" data-id="element-504">
              Temps total de déplacement
            </p>
          </div>
        </div>
      </div>
      {/* Optimized Schedule Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-505">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-506">
          Planning optimisé
        </h3>
        <div className="space-y-4" data-id="element-507">
          {optimizedSchedule.map(intervention => {
          const client = clients.find(c => c.id === intervention.clientId);
          const forecast = forecasts.find(f => f.id === intervention.forecastId);
          if (!client || !forecast) return null;
          return <div key={intervention.forecastId} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-508">
                <div className="flex items-start justify-between" data-id="element-509">
                  <div className="flex items-start space-x-4 flex-1" data-id="element-510">
                    {/* Order Badge */}
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-511">
                      <span className="text-lg font-bold text-blue-600" data-id="element-512">
                        {intervention.order}
                      </span>
                    </div>
                    {/* Client Info */}
                    <div className="flex-1" data-id="element-513">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-514">
                        <h4 className="text-sm font-semibold text-gray-900" data-id="element-515">
                          {client.name}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${client.priority === 'high' ? 'bg-red-100 text-red-700' : client.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-516">
                          {client.priority === 'high' ? 'Haute' : client.priority === 'medium' ? 'Moyenne' : 'Basse'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-gray-600 mb-3" data-id="element-517">
                        <MapPin className="w-3 h-3" data-id="element-518" />
                        <span data-id="element-519">{client.address.city}</span>
                      </div>
                      {/* Timeline */}
                      <div className="space-y-2" data-id="element-520">
                        {/* Snow Event */}
                        <div className="flex items-center space-x-2 text-xs" data-id="element-521">
                          <div className="w-2 h-2 bg-blue-400 rounded-full" data-id="element-522"></div>
                          <span className="text-gray-600" data-id="element-523">Neige:</span>
                          <span className="font-medium text-gray-900" data-id="element-524">
                            {format(forecast.eventStart, 'HH:mm', {
                          locale: fr
                        })}{' '}
                            →{' '}
                            {format(forecast.eventEnd, 'HH:mm', {
                          locale: fr
                        })}
                          </span>
                          <span className="text-gray-500" data-id="element-525">
                            ({forecast.accumulation.median} cm)
                          </span>
                        </div>
                        {/* Intervention */}
                        <div className="flex items-center space-x-2 text-xs" data-id="element-526">
                          <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-527"></div>
                          <span className="text-gray-600" data-id="element-528">Intervention:</span>
                          <span className="font-medium text-gray-900" data-id="element-529">
                            {format(intervention.startTime, 'HH:mm', {
                          locale: fr
                        })}{' '}
                            →{' '}
                            {format(intervention.endTime, 'HH:mm', {
                          locale: fr
                        })}
                          </span>
                          <span className="text-gray-500" data-id="element-530">
                            ({intervention.duration} min)
                          </span>
                        </div>
                        {intervention.travelTime > 0 && <div className="flex items-center space-x-2 text-xs" data-id="element-531">
                            <div className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-532"></div>
                            <span className="text-gray-600" data-id="element-533">
                              Temps de trajet:
                            </span>
                            <span className="text-gray-900" data-id="element-534">
                              {intervention.travelTime} min
                            </span>
                          </div>}
                      </div>
                    </div>
                  </div>
                  {/* Metrics */}
                  <div className="flex flex-col items-end space-y-2" data-id="element-535">
                    <div className="flex items-center space-x-2" data-id="element-536">
                      <span className="text-xs text-gray-600" data-id="element-537">Efficacité:</span>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${intervention.efficiency >= 80 ? 'bg-green-100 text-green-700' : intervention.efficiency >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-538">
                        {intervention.efficiency.toFixed(0)}%
                      </div>
                    </div>
                    {intervention.slaCompliance ? <div className="flex items-center space-x-1 text-xs text-green-700" data-id="element-539">
                        <CheckCircle className="w-3 h-3" data-id="element-540" />
                        <span data-id="element-541">SLA respecté</span>
                      </div> : <div className="flex items-center space-x-1 text-xs text-red-700" data-id="element-542">
                        <AlertCircle className="w-3 h-3" data-id="element-543" />
                        <span data-id="element-544">SLA dépassé</span>
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
      {/* Optimization Tips */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-545">
        <div className="flex items-start space-x-3" data-id="element-546">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-547" />
          <div data-id="element-548">
            <h4 className="text-sm font-semibold text-blue-900 mb-1" data-id="element-549">
              Conseils d'optimisation
            </h4>
            <ul className="text-xs text-blue-800 space-y-1" data-id="element-550">
              <li data-id="element-551">
                • Les interventions sont planifiées pour terminer au plus près
                de la fin de la chute de neige
              </li>
              <li data-id="element-552">
                • Les clients haute priorité sont traités en premier avec une
                marge de sécurité
              </li>
              <li data-id="element-553">
                • Le parcours minimise les temps de trajet entre les sites
              </li>
              <li data-id="element-554">
                • Les contraintes SLA sont respectées pour tous les clients
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
}